import React, {
    useReducer, useEffect, useCallback, useRef, useState,
} from 'react';
import Container from 'components/widgets/Container';
import {
    Grid, Chip,
    makeStyles, Button, Dialog, DialogContent, AppBar, Toolbar, IconButton, Typography, Slide, useTheme, useMediaQuery,
} from '@material-ui/core';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import LoadingMask from 'components/widgets/LoadingMask';
import AccountingStyles from 'styles/modules/accounting/AccountingStyles';
import CloseIcon from '@material-ui/icons/Close';
import { SaveIcon, BackupIcon } from 'components/icons';
import { Row, Form, Col } from 'react-bootstrap';
import InputNumber from 'components/widgets/InputNumber';
import { isValidField, isValidSchema } from 'utils/schema/utils';
import TransferSchema from 'utils/schema/accounting/banking/TransferSchema';
import ModalUtils from 'utils/ModalUtils';
import DialogActions from '@material-ui/core/DialogActions';
import { useHistory, useParams } from 'react-router';
import { modules, DOCUMENT_TYPES_AVAILABLE_MODULES } from 'utils/enum/modules';
import { AccountingCBStatus, AccountingSubModules, AccountingCOAType } from 'utils/enum/AccountingEnum';
import DropdownQuery from 'components/widgets/DropdownQuery';
import AccountingCOAQuery from 'services/graphQL/query/accounting/AccountingCOAQuery';
import LotQuery from 'services/graphQL/query/LotQuery';
import { useLazyQuery, useMutation } from '@apollo/client';
import DocumentTypeMutation from 'services/graphQL/mutate/DocumentTypeMutation';
import AccountingMutation from 'services/graphQL/mutate/accounting/AccountingMutation';
import AccountingQuery from 'services/graphQL/query/accounting/AccountingQuery';
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';
import clsx from 'clsx';
import PaperComponent from 'components/modules/accounting/PaperComponent';
import DialogActionMessage from 'components/widgets/DialogActionMessage';
import DatePicker from 'react-datepicker';
import DateUtils from 'lib/DateUtils';
import TableUtils from 'utils/TableUtils';
import { FetchPolicy, DOCUMENT_REFERENCE_TYPE } from 'utils/enum/Core';
import CalendarContainer from 'components/widgets/form/CalendarContainer';
import FileManagerContainer from 'components/widgets/filemanager/FileManagerContainer';
import useDocumentsOtherFunctions from 'components/hook/core/useDocumentsOtherFunctions';
// todo: check why the datepicker doesn't work
import 'react-datepicker/dist/react-datepicker.css';

const useStyle = makeStyles((theme) => AccountingStyles.dialogForm(theme));

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export const ACTION_TYPES = {
    SET_RECORD: 'setRecord',
    SET_RECORD_CHANGED: 'setRecordChanged',
    SET_RECORD_VALUE: 'setRecordValue',
    SET_ON_POPUP_CLOSE: 'setOnPopupClose',
    SET_INITIAL_STATE: 'setInitialState',
};

const TransferReducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPES.SET_RECORD: {
        return {
            ...state,
            record: action.value,
            isNew: action.value.cbId === 0,
        };
    }
    case ACTION_TYPES.SET_RECORD_CHANGED: {
        return { ...state, record: action.value, isDirty: true };
    }
    case ACTION_TYPES.SET_RECORD_VALUE: {
        return { ...state, [action.value.key]: action.value.value };
    }
    case ACTION_TYPES.SET_ON_POPUP_CLOSE: {
        return {
            ...state,
            couldLostData: true,
        };
    }
    case ACTION_TYPES.SET_INITIAL_STATE: {
        return action.value;
    }
    default:
        return state;
    }
};

const keyStore = new KeyStore();

const TransferCreate = () => {
    const ACCOUNTING_BANKING_TRANSFER_WRITE = keyStore.hasPermission(Permission.ACCOUNTING_BANKING_TRANSFER_WRITE);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isPhone = useMediaQuery(theme.breakpoints.down('xs'));
    let accountLotCols = 4;
    if (isMobile) accountLotCols = 6;
    if (isPhone) accountLotCols = 12;
    const accountColStyle = !isMobile ? { paddingLeft: 4, paddingRight: 4 } : {};
    const lotColStyle = !isMobile ? { paddingLeft: 4 } : {};
    const lotMobileSpacingStyle = isPhone ? { marginTop: 8 } : {};

    const { id } = useParams();
    const history = useHistory();

    const { updateDocumentsReferenceId, deleteDocumentsByTemporaryReference } = useDocumentsOtherFunctions({
        updateMutation: AccountingMutation.UPDATE_ACCOUNTING_DOCUMENTS_REFERENCE_ID,
        deleteMutation: AccountingMutation.DELETE_ACCOUNTING_DOCUMENTS_TEMPORARY_REFERENCE,
    });

    const initialState = {
        record: {
            cbId: 0,
            fromBankAccountId: 0,
            fromLotId: 0,
            toBankAccountId: 0,
            toLotId: 0,
            amount: 0,
            status: AccountingCBStatus.NEW,
            date: new Date(),
        },
        isDirty: false,
        isNew: true,
        couldLostData: false,
        temporaryReferenceId: TableUtils.generateUUID(),
    };

    const classes = useStyle();

    const [state, dispatch] = useReducer(TransferReducer, initialState);
    const {
        record, isDirty, couldLostData, temporaryReferenceId,
    } = state;

    const recordRef = useRef(record);
    const [bankAccountLotMap, setBankAccountLotMap] = useState({});
    const [lotOptions, setLotOptions] = useState([]);
    const [isFromAccountLoaded, setIsFromAccountLoaded] = useState(false);
    const [isToAccountLoaded, setIsToAccountLoaded] = useState(false);
    const [isFromLotLoaded, setIsFromLotLoaded] = useState(false);
    const [isToLotLoaded, setIsToLotLoaded] = useState(false);

    const isValidData = isValidSchema(TransferSchema, record);
    const { isValid, errors } = isValidData;

    const [getTransfer, { loading }] = useLazyQuery(AccountingQuery.GET_ACCOUNTING_TRANSFER_CB, {
        onCompleted: (res) => {
            if (res.getTransfer) {
                const transferRecord = res.getTransfer;

                dispatch({
                    type: ACTION_TYPES.SET_RECORD,
                    value: {
                        ...transferRecord,
                        fromLotId: transferRecord.fromLotId || 0,
                        toLotId: transferRecord.toLotId || 0,
                        date: new Date(DateUtils.getOnlyDate(transferRecord.date)),
                    },
                });
            }
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    useEffect(() => {
        if (id > 0) {
            getTransfer({
                variables: {
                    cbId: Number(id),
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const onClearForm = () => {
        dispatch({
            type: ACTION_TYPES.SET_INITIAL_STATE,
            value: initialState,
        });
    };

    const onPopupClose = (event, forceClose = false) => {
        if (isDirty && !forceClose) {
            dispatch(
                {
                    type: ACTION_TYPES.SET_ON_POPUP_CLOSE,
                },
            );
            return;
        }

        if ((id ?? 0) === 0) {
            // Remove temporary documents created
            deleteDocumentsByTemporaryReference({
                variables: {
                    temporaryName: temporaryReferenceId,
                },
            });
        }

        onClearForm();
        history.push(`/${modules.ACCOUNTING}/${AccountingSubModules.BANKING}`);
    };

    const [addRecord, { loading: isSaving }] = useMutation(AccountingMutation.CREATE_UPDATE_TRANSFER, {
        onCompleted: async (mutationData) => {
            const createdCbId = mutationData?.createUpdateTransfer;
            if (createdCbId > 0) {
                ModalUtils.successMessage(null, 'Funds have been transferred successfully!');

                // Update referenceId of documents
                if ((id ?? 0) === 0) {
                    await updateDocumentsReferenceId({
                        variables: {
                            referenceId: createdCbId,
                            temporaryName: temporaryReferenceId,
                        },
                    });
                }

                onPopupClose(null, true);
            }
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage(null, errorMessage);
        },
    });

    const onSave = async () => {
        const currentRecord = { ...record, date: DateUtils.isValid(record.date) ? DateUtils.toLocal(record.date).toDate() : '' };
        delete currentRecord.status;

        await addRecord({
            variables: {
                record: currentRecord,
            },
        });
    };

    const getRecordListFromRootData = useCallback((data, rootData) => {
        const rootArray = rootData.split('.');
        let recordList = data;

        rootArray.forEach((item) => {
            recordList = recordList[item];
        });

        return recordList;
    }, []);

    const setFromAccountData = useCallback((data, rootData) => {
        const recordList = getRecordListFromRootData(data, rootData) || [];
        const nextBankAccountLotMap = recordList.reduce((acc, item) => {
            acc[item.accountNumber] = item.lotName;
            return acc;
        }, {});

        setBankAccountLotMap(nextBankAccountLotMap);
        setIsFromAccountLoaded(true);
        return recordList;
    }, [getRecordListFromRootData]);

    const setToAccountData = useCallback((data, rootData) => {
        const recordList = getRecordListFromRootData(data, rootData) || [];
        const nextBankAccountLotMap = recordList.reduce((acc, item) => {
            acc[item.accountNumber] = item.lotName;
            return acc;
        }, {});

        setBankAccountLotMap(nextBankAccountLotMap);
        setIsToAccountLoaded(true);
        return recordList;
    }, [getRecordListFromRootData]);

    const setFromLotData = useCallback((data, rootData) => {
        const recordList = getRecordListFromRootData(data, rootData) || [];
        setLotOptions(recordList);
        setIsFromLotLoaded(true);
        return recordList;
    }, [getRecordListFromRootData]);

    const setToLotData = useCallback((data, rootData) => {
        const recordList = getRecordListFromRootData(data, rootData) || [];
        setLotOptions(recordList);
        setIsToLotLoaded(true);
        return recordList;
    }, [getRecordListFromRootData]);

    const getLotIdFromLotName = useCallback((lotName, currentLotId = 0) => {
        if (!lotName || !lotOptions.length) return currentLotId;

        const lotMatch = lotOptions.find((item) => String(item.lotName).toLowerCase() === String(lotName).toLowerCase());
        return lotMatch?.lotId ?? 0;
    }, [lotOptions]);

    const onChangeValue = (field, value, additionalFields = {}) => {
        if (value === record[field]) return;

        const currentRecord = { ...record };

        currentRecord[field] = value;

        if (field === 'fromBankAccountId') {
            const lotName = additionalFields?.lotName || bankAccountLotMap[currentRecord.fromBankAccountId];
            currentRecord.fromLotId = getLotIdFromLotName(lotName, currentRecord.fromLotId);
        }

        if (field === 'toBankAccountId') {
            const lotName = additionalFields?.lotName || bankAccountLotMap[currentRecord.toBankAccountId];
            currentRecord.toLotId = getLotIdFromLotName(lotName, currentRecord.toLotId);
        }

        dispatch({
            type: ACTION_TYPES.SET_RECORD_CHANGED,
            value: currentRecord,
        });
    };

    useEffect(() => {
        recordRef.current = record;
    }, [record]);

    useEffect(() => {
        if (!isFromAccountLoaded || !isFromLotLoaded || !record.fromBankAccountId) return;

        const currentRecord = recordRef.current;
        // Preserve persisted lot from backend on edit mode.
        if (currentRecord.cbId > 0 && currentRecord.fromLotId > 0) return;

        const lotName = bankAccountLotMap[record.fromBankAccountId];
        const lotId = getLotIdFromLotName(lotName, 0);
        if (lotId === currentRecord.fromLotId) return;

        dispatch({
            type: ACTION_TYPES.SET_RECORD,
            value: {
                ...currentRecord,
                fromLotId: lotId,
            },
        });
    }, [isFromAccountLoaded, isFromLotLoaded, record.fromBankAccountId, bankAccountLotMap, getLotIdFromLotName]);

    useEffect(() => {
        if (!isToAccountLoaded || !isToLotLoaded || !record.toBankAccountId) return;

        const currentRecord = recordRef.current;
        // Preserve persisted lot from backend on edit mode.
        if (currentRecord.cbId > 0 && currentRecord.toLotId > 0) return;

        const lotName = bankAccountLotMap[record.toBankAccountId];
        const lotId = getLotIdFromLotName(lotName, 0);
        if (lotId === currentRecord.toLotId) return;

        dispatch({
            type: ACTION_TYPES.SET_RECORD,
            value: {
                ...currentRecord,
                toLotId: lotId,
            },
        });
    }, [isToAccountLoaded, isToLotLoaded, record.toBankAccountId, bankAccountLotMap, getLotIdFromLotName]);

    const getStatusChip = () => {
        if (record?.status.toLowerCase() === AccountingCBStatus.NEW.toLowerCase()) return null;

        let badgeStatus = classes.primary;
        if (record?.status.toLowerCase() === AccountingCBStatus.APPROVED.toLowerCase()) badgeStatus = classes.badgeApproved;
        else if (record?.status.toLowerCase() === AccountingCBStatus.VOID.toLowerCase()) badgeStatus = classes.badgeVoid;
        else if (record?.status.toLowerCase() === AccountingCBStatus.CLEARED.toLowerCase()) badgeStatus = classes.badgeCleared;
        else if (record?.status.toLowerCase() === AccountingCBStatus.PRINTED.toLowerCase()) badgeStatus = classes.badgePrinted;

        return (
            <>
                <span className={classes.fontSmall}>Status: </span>
                <Chip size="small" label={record.status} className={badgeStatus} />
            </>
        );
    };

    const allowToSave = ACCOUNTING_BANKING_TRANSFER_WRITE && (record.status.toLowerCase() === AccountingCBStatus.POSTED.toLowerCase()
    || record.status.toLowerCase() === AccountingCBStatus.NEW.toLowerCase());

    return (
        <Dialog
            open
            onClose={onPopupClose}
            maxWidth="sm"
            fullWidth
            disableBackdropClick
            disableEscapeKeyDown
            scroll="paper"
            TransitionComponent={Transition}
            PaperComponent={PaperComponent}
        >
            <AppBar className={classes.appBarMove}>
                <Toolbar className={classes.centerItems}>
                    <Typography variant="h6" className={classes.title}>
                        Transfer Funds Between Accounts
                    </Typography>
                    <div className={classes.centerItems}>
                        <IconButton edge="start" color="inherit" onClick={onPopupClose}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                </Toolbar>
            </AppBar>
            <DialogContent className={classes.noPadding}>
                { loading && <LoadingMask />}
                {!loading && (
                    <Container className={clsx(classes.containerSplit, classes.paddingSides15)}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <Form.Group as={Row}>
                                    <Form.Label
                                        column="sm"
                                        xs={isMobile ? 12 : 4}
                                        className="required"
                                        style={!isMobile ? { paddingRight: 4 } : {}}
                                    >
                                        From Bank Account
                                    </Form.Label>
                                    <Col xs={accountLotCols} style={accountColStyle}>
                                        <DropdownQuery
                                            name="fromBankAccountId"
                                            className={isValidField(errors, 'fromBankAccountId') ? 'invalid-field' : ''}
                                            value={record.fromBankAccountId}
                                            defaultEmptyLineText="Select Account"
                                            onChange={(name, newValue, _, additionalFields) => onChangeValue(name, newValue, additionalFields)}
                                            dataSource={{
                                                query: AccountingCOAQuery.GET_ACCOUNTING_COA_LIST,
                                                variables: {
                                                    paginate: {
                                                        init: 0,
                                                        ignoreLimit: true,
                                                    },
                                                    filters: {
                                                        accountTypes: [AccountingCOAType.BANK, AccountingCOAType.CASH],
                                                    },
                                                },
                                                rootData: 'getAccountingCOAList.data',
                                                idField: 'accountNumber',
                                                descriptionField: 'fullDescription',
                                                additionalFieldsReturned: ['lotName'],
                                            }}
                                            customDataHandler={setFromAccountData}
                                            disabled={!allowToSave}
                                        />
                                    </Col>
                                    <Col xs={accountLotCols} style={{ ...lotColStyle, ...lotMobileSpacingStyle }}>
                                        <DropdownQuery
                                            name="fromLotId"
                                            className={isValidField(errors, 'fromLotId') ? 'invalid-field' : ''}
                                            value={record.fromLotId || 0}
                                            defaultEmptyLineText="Select Lot"
                                            onChange={(name, newValue) => onChangeValue(name, newValue)}
                                            dataSource={{
                                                query: LotQuery.GET_LOTS,
                                                rootData: 'lotList',
                                                idField: 'lotId',
                                                descriptionField: 'lotName',
                                            }}
                                            customDataHandler={setFromLotData}
                                            disabled={!allowToSave}
                                        />
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row}>
                                    <Form.Label
                                        column="sm"
                                        xs={isMobile ? 12 : 4}
                                        className="required"
                                        style={!isMobile ? { paddingRight: 4 } : {}}
                                    >
                                        To Bank Account
                                    </Form.Label>
                                    <Col xs={accountLotCols} style={accountColStyle}>
                                        <DropdownQuery
                                            name="toBankAccountId"
                                            className={isValidField(errors, 'toBankAccountId') ? 'invalid-field' : ''}
                                            value={record.toBankAccountId}
                                            defaultEmptyLineText="Select Account"
                                            onChange={(name, newValue, _, additionalFields) => onChangeValue(name, newValue, additionalFields)}
                                            dataSource={{
                                                query: AccountingCOAQuery.GET_ACCOUNTING_COA_LIST,
                                                variables: {
                                                    paginate: {
                                                        init: 0,
                                                        ignoreLimit: true,
                                                    },
                                                    filters: {
                                                        accountTypes: [AccountingCOAType.BANK, AccountingCOAType.CASH],
                                                    },
                                                },
                                                rootData: 'getAccountingCOAList.data',
                                                idField: 'accountNumber',
                                                descriptionField: 'fullDescription',
                                                additionalFieldsReturned: ['lotName'],
                                            }}
                                            customDataHandler={setToAccountData}
                                            disabled={!allowToSave}
                                        />
                                    </Col>
                                    <Col xs={accountLotCols} style={{ ...lotColStyle, ...lotMobileSpacingStyle }}>
                                        <DropdownQuery
                                            name="toLotId"
                                            className={isValidField(errors, 'toLotId') ? 'invalid-field' : ''}
                                            value={record.toLotId || 0}
                                            defaultEmptyLineText="Select Lot"
                                            onChange={(name, newValue) => onChangeValue(name, newValue)}
                                            dataSource={{
                                                query: LotQuery.GET_LOTS,
                                                rootData: 'lotList',
                                                idField: 'lotId',
                                                descriptionField: 'lotName',
                                            }}
                                            customDataHandler={setToLotData}
                                            disabled={!allowToSave}
                                        />
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row}>
                                    <Form.Label
                                        column="sm"
                                        xs={isMobile ? 12 : 4}
                                        className="required"
                                        style={!isMobile ? { paddingRight: 4 } : {}}
                                    >
                                        Amount
                                    </Form.Label>
                                    <Col xs={isMobile ? 12 : 8} style={!isMobile ? { paddingLeft: 4 } : {}}>
                                        <InputNumber
                                            size="sm"
                                            showCurrency
                                            value={record.amount}
                                            className={isValidField(errors, 'amount') ? 'invalid-field' : ''}
                                            thousandSeparator
                                            placeholder="Cash"
                                            onChange={(value) => onChangeValue('amount', value)}
                                            disabled={!allowToSave}
                                        />
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row}>
                                    <Form.Label
                                        column="sm"
                                        xs={isMobile ? 12 : 4}
                                        className="required"
                                        style={!isMobile ? { paddingRight: 4 } : {}}
                                    >
                                        Date
                                    </Form.Label>
                                    <Col xs={isMobile ? 12 : 8} style={!isMobile ? { paddingLeft: 4 } : {}}>
                                        <DatePicker
                                            selected={record.date}
                                            disabled={!allowToSave}
                                            name="date"
                                            size="sm"
                                            wrapperClassName={classes.control100}
                                            className={clsx('form-control form-control-sm', isValidField(errors, 'date') ? 'invalid-field' : '')}
                                            placeholderText="mm/dd/yyyy"
                                            popperContainer={CalendarContainer}
                                            onChange={(date) => onChangeValue('date', date)}
                                        />
                                    </Col>
                                </Form.Group>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Form.Group className={classes.documents} as={Col}>
                                <Form.Label className="required">Files Attachments</Form.Label>
                                <FileManagerContainer
                                    height={450}
                                    previewOnDialog
                                    documentsProps={[
                                        {
                                            temporaryReferenceId,
                                            referenceId: Number(id ?? 0),
                                            referenceType: DOCUMENT_REFERENCE_TYPE.TRANSFER_FUNDS,
                                            referenceName: 'referenceId',
                                            module: DOCUMENT_TYPES_AVAILABLE_MODULES.ACCOUNTING,
                                            pullDocumentsQuery: AccountingQuery.GET_ACCOUNTING_DOCUMENTS,
                                            getDocumentUploadSignedURL: AccountingQuery.GET_ACCOUNTING_DOCUMENT_UPLOAD_SIGNED_URL,
                                            createDocumentsMutation: AccountingMutation.CREATE_ACCOUNTING_DOCUMENTS,
                                            editDocumentNameMutation: AccountingMutation.EDIT_ACCOUNTING_DOCUMENT_NAME,
                                            deleteMutation: AccountingMutation.DELETE_ACCOUNTING_DOCUMENT,
                                            canWrite: ACCOUNTING_BANKING_TRANSFER_WRITE,
                                            canDelete: ACCOUNTING_BANKING_TRANSFER_WRITE,
                                            documentTypeMutation: DocumentTypeMutation.UPDATE_FILE_DOCUMENT_TYPE,
                                            hiddenColumns: ['Size', 'Extension'],
                                            customUploadButton: (customClass) => (
                                                <div className={customClass}>
                                                    <BackupIcon />
                                                    <p>
                                                        Drag & drop files here or click to upload
                                                    </p>
                                                </div>
                                            ),
                                            useShortColumns: true,
                                            showFileExtension: true,
                                        },
                                    ]}
                                />
                            </Form.Group>
                        </Grid>
                        <ConfirmDialog
                            title="Attention - You have unsaved changes!"
                            description="Do you want to close without saving?"
                            open={couldLostData}
                            variant="outlined"
                            titlePrimary="Close"
                            titleSecondary="Cancel"
                            onClose={() => dispatch({ type: ACTION_TYPES.SET_RECORD_VALUE, value: { key: 'couldLostData', value: false } })}
                            onClickSecondary={() => dispatch({ type: ACTION_TYPES.SET_RECORD_VALUE, value: { key: 'couldLostData', value: false } })}
                            onClickPrimary={(e) => onPopupClose(e, true)}
                        />
                    </Container>
                )}
            </DialogContent>
            <DialogActions className={classes.borderTop}>
                <div className={classes.fullWidth}>
                    <Toolbar className={classes.centerItems}>
                        <div className={classes.buttonSpacing}>
                            { getStatusChip() }
                        </div>
                        <div className={classes.buttonSpacing}>
                            {allowToSave && (
                                <Button
                                    className={(isValid && isDirty) ? classes.buttonSave : classes.buttonDisable}
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    size="small"
                                    disabled={!(isValid && isDirty) || isSaving}
                                    onClick={onSave}
                                >
                                    Save
                                </Button>
                            )}
                        </div>
                    </Toolbar>
                </div>
            </DialogActions>
            {isSaving && <DialogActionMessage message="Saving transfer information... " />}
        </Dialog>
    );
};

export default TransferCreate;
